import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/track_usage"
import { hide, show, getUrlAnchor, scrollToAnchor } from "../lib/helpers"
import { log } from "../lib/log"
import { StoredSetting } from "../lib/stored_setting"
import { afterMorph } from "../lib/after_morph"

const EXPAND = "expand"
const COLLAPSE = "collapse"
const EXPANDED_OR_COLLAPSED_KEY = "expanded-or-collapsed-items"
const CONTROL_DEFAULTS_KEY = "expanded-or-collapsed-control-default-items"

const TRIGGER_SELECTOR = "[data-action*='expand-or-collapse#']"

// Nesting is common (a focus area holds one of these for its vision), so only
// claim the triggers whose nearest controller root is ours.
const ownTriggers = (parentElement) =>
  [...parentElement.querySelectorAll(TRIGGER_SELECTOR)]
    .filter(el => el.closest("[data-controller~='expand-or-collapse']") === parentElement)

const eagerLoadChildTurboFrames = (parentElement) => {
  parentElement
    .querySelectorAll("turbo-frame[loading='lazy']")
    .forEach(tf => {
      if (tf.closest("[data-controller~='expand-or-collapse']") == parentElement) {
        tf.setAttribute("loading", "eager")
      }
    })
}

export default class extends Controller {
  static targets = ["hideWhenExpanded", "hideWhenCollapsed"]
  static values = {
    identifier: String,
    controlDefault: String,
    eagerLoadWait: {
      type: Number,
      default: 150
    }
  }

  disconnect() {
    this.stopAfterMorph()
    this.element.removeEventListener("keydown", this.keydownHandler)
    log("expandOrCollapse: disconnect")
  }

  connect() {
    log("expandOrCollapse: connect")

    if (!this.hasHideWhenExpandedTarget || !this.hasHideWhenCollapsedTarget) {
      log("Expand or Collapse Controller: Should have at least one hideWhenExpandedTargets and one hideWhenCollapsedTargets", this.identifierValue)
    }

    // Reset stale remembered state BEFORE the anchor check, so an
    // anchor-driven EXPAND below isn't wiped by the reset.
    this.resetStoredStateIfDefaultChanged()

    // Expand if child element has anchor tag pointing to it
    const anchor = getUrlAnchor()
    if (anchor && this.element.querySelector(anchor)) {
      StoredSetting.set(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue, EXPAND)
      this.expand()
      scrollToAnchor()
    }

    this.applyStoredState()

    // A morph (page refresh or a refresh="morph" frame) resets our targets to
    // the server-rendered (collapsed) markup without reconnecting this
    // controller, so re-apply the stored state after every morph.
    this.stopAfterMorph = afterMorph(this.element, () => this.applyStoredState())

    // Delegated so triggers arriving with a morph are covered too.
    this.keydownHandler = (event) => this.activateTrigger(event)
    this.element.addEventListener("keydown", this.keydownHandler)
  }

  // Triggers are icons, anchors and <div>s spread across a dozen templates;
  // labelling them here beats rewriting every call site as a <button>. Both
  // halves of an expand/collapse pair get the same state — only one of the
  // pair is ever visible, the other is display:none via d-none.
  syncTriggers(expanded) {
    ownTriggers(this.element).forEach(trigger => {
      trigger.setAttribute("aria-expanded", expanded)

      if (trigger.tagName !== "BUTTON") {
        trigger.setAttribute("role", "button")
        trigger.setAttribute("tabindex", "0")
      }
    })
  }

  // role="button" has to answer to Enter and Space (WCAG 2.1.1).
  activateTrigger(event) {
    if (event.key !== "Enter" && event.key !== " ") return

    const trigger = event.target.closest?.(TRIGGER_SELECTOR)
    if (!trigger || !ownTriggers(this.element).includes(trigger)) return

    event.preventDefault()
    trigger.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }))
  }

  // If the server-side default changed (possibly during a morph, e.g.
  // saving display_expanded_vision_on_load), reset the remembered state so
  // the new default wins, and persist the new default immediately.
  resetStoredStateIfDefaultChanged() {
    if (this.rememberedControlDefault !== this.controlDefaultValue) {
      StoredSetting.remove(CONTROL_DEFAULTS_KEY, this.identifierValue)
      StoredSetting.remove(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue)
    }

    StoredSetting.set(CONTROL_DEFAULTS_KEY, this.identifierValue, this.controlDefaultValue)
  }

  applyStoredState() {
    this.resetStoredStateIfDefaultChanged()

    // When remembered as expanded
    if (this.isRememberedAsExpanded) {
      this.expand()
      return
    }

    // When remembered as collapsed
    if (this.isRememberedAsCollapsed) {
      this.collapse()
      return
    }

    // When relying on defaults
    if (this.isExpandingControlDefault) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  get isExpandingControlDefault() {
    return !this.isCollapsingControlDefault
  }

  get isRememberedAsExpanded() {
    return StoredSetting.get(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue) === EXPAND
  }

  get isCollapsingControlDefault() {
    return this.controlDefaultValue === COLLAPSE
  }

  get isRememberedAsCollapsed() {
    return StoredSetting.get(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue) === COLLAPSE
  }

  get rememberedControlDefault() {
    return StoredSetting.get(CONTROL_DEFAULTS_KEY, this.identifierValue)
  }

  expand(event) {
    event?.stopImmediatePropagation()
    event?.preventDefault()

    eagerLoadChildTurboFrames(this.element)

    hide(this.hideWhenExpandedTargets)
    show(this.hideWhenCollapsedTargets)
    this.syncTriggers(true)

    this.remember(EXPAND)
    if (event) this.trackToggle("expanded")
  }

  collapse(event) {
    event?.stopImmediatePropagation()
    event?.preventDefault()

    hide(this.hideWhenCollapsedTargets)
    show(this.hideWhenExpandedTargets)
    this.syncTriggers(false)

    this.remember(COLLAPSE)
    if (event) this.trackToggle("collapsed")
  }

  // `event` is only present on a user click; connect() and morphs call these bare.
  trackToggle(state) {
    track("section_toggled", { state, kind: this.identifierValue.replace(/[-_]?\d+$/, "") })
  }

  loadChildTurboFramesStart(event) {
    this.eagerLoadingTimer =
      setTimeout(
        () => {
          eagerLoadChildTurboFrames(this.element)
          this.eagerLoadingTimer = null
        },
        this.eagerLoadWaitValue
      )
  }

  loadChildTurboFramesEnd(event) {
    if (this.eagerLoadingTimer) {
      clearTimeout(this.eagerLoadingTimer)
      this.eagerLoadingTimer = null
    }
  }

  remember(value) {
    StoredSetting.set(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue, value)
  }
}